import React from 'react';

import { makeStyles } from 'tss-react/mui';
import { Skeleton } from '@mui/material';
import { MatchingProfileData, MatchingProvidersType } from '@uplift/api';
import { ProviderMedicalRole } from '@uplift/types';

import { NoProviders } from './no-providers';
import { ProviderItem } from './provider-item/provider-item';
import { ShowMore } from './show-more';

export type ProviderListProps = {
  matchingProfiles: MatchingProfileData[];
  insuranceCompanyId: string;
  providersToShow: number;
  onLoadMore: () => void;
  onViewClick?: (selectedProvider: MatchingProfileData) => void;
  stateId: string;
  isLoading?: boolean;
  medicalRole?: ProviderMedicalRole;
  matchingPaymentType?: MatchingProvidersType;
  emptyListRelaxed?: boolean;
  noInsuranceProviders?: boolean;
  isBookingSponsored?: boolean;
  specialtyMatches?: string[];
  missingExperienceFilters?: string[];
};

export const ProviderList: React.VFC<ProviderListProps> = ({
  medicalRole,
  matchingProfiles,
  insuranceCompanyId,
  providersToShow,
  onLoadMore,
  onViewClick,
  stateId,
  matchingPaymentType,
  isLoading,
  noInsuranceProviders = false,
  isBookingSponsored = false,
  specialtyMatches,
  missingExperienceFilters
}) => {
  const { classes } = useSelectProviderListStyles();

  const showNoProviders =
    Boolean(noInsuranceProviders || missingExperienceFilters?.length || specialtyMatches?.length || !matchingProfiles?.length)
    && !isLoading;

  return (
    <div className={classes.root}>
      {isLoading && Array.from({ length: 3 }).map((_, index) => (
        <Skeleton key={index} variant="rectangular" height={294} className={classes.providerItemSkeleton} />
      ))}
      {showNoProviders && (
        <NoProviders
          medicalRole={medicalRole}
          numProviders={matchingProfiles?.length}
          noInsuranceProviders={noInsuranceProviders}
          classes={{ container: classes.noProviderContainer }}
          specialtyMatches={specialtyMatches}
          missingExperienceFilters={missingExperienceFilters}
        />
      )}
      {matchingProfiles.slice(0, providersToShow).map(matchingProfile => (
        <ProviderItem
          stateId={stateId}
          preferredInsuranceCompanyId={matchingPaymentType === MatchingProvidersType.cashpay ? null : insuranceCompanyId}
          matchingProfile={matchingProfile}
          onView={() => onViewClick(matchingProfile)}
          key={matchingProfile.user.id}
          classes={{ root: classes.providerItemRoot }}
          isBookingSponsored={isBookingSponsored}
        />
      ))}
      {(providersToShow < matchingProfiles.length) && <ShowMore onLoadMore={onLoadMore} />}
    </div>
  );
};

const useSelectProviderListStyles = makeStyles({ name: 'SelectProviderList' })(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2, -2, 0),

    [theme.breakpoints.up('xs')]: {
      margin: theme.spacing(4, 0, 0),
    },

    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(5, 0, 0),
    }
  },
  noProviderContainer: {
    marginTop: theme.spacing(3),
  },
  providerItemRoot: {
    background: theme.palette.background.paper,
  },
  providerItemSkeleton: {
    '&:not(:first-of-type)': {
      marginTop: theme.spacing(4),
    },
  },
}));
